import type { ReviewMode } from "../fuxi/modes.js";
import type { CardSubject } from "../fuxi/review-card.js";
import type { HuiguPeriod } from "./huigu-period.js";
import {
  type SittingShape,
  replayAtMost,
  sittingShapeFields,
} from "../fuxi/sitting-shape.js";

/**
 * Where 复习 answers, and the names its form fields go by.
 *
 * A file of constants rather than a section of the markup, because the route
 * and the markup both need every one of them and neither should import the
 * other. `save-word-markup.ts` holds `ciPath` for the same reason.
 */

/**
 * Where a session is drawn and where an answer is posted.
 *
 * One address for both, where `/user/pro/` is a `POST` with no page of its own. A session
 * is one page that keeps answering itself: the reader is shown a card, posts
 * what they typed, and is shown the marking of it at the same address. Two
 * paths would make the marked answer a page a reload could not reproduce.
 *
 * The trailing slash is the site's form for every address, and the Function at
 * the edge answers the unslashed form with a 308 that keeps the method and the
 * fields. See "Every route here ends in a slash" in docs/user-features.md.
 */
export const fuxiPath = "/user/fuxi/";

/**
 * Where a reader reports a problem with a marked card.
 *
 * Its own address rather than another field on the answer, because a report
 * moves no card and answering one must never be mistaken for an answer.
 * `./fuxi-reporting.ts` takes it.
 */
export const fuxiReportPath = "/user/fuxi/report/";

/**
 * Where the next card is drawn, carrying the shape the sitting is in.
 *
 * The cue words just answered and the run in progress, as
 * `src/fuxi/sitting-shape.ts` writes them. Hints on the way to the next card
 * rather than anything kept between requests. A `GET` that carries neither,
 * which is every arrival at the page and every reload, draws whatever is
 * soonest due. Nothing is lost by leaving them off.
 *
 * @param shape - The shape for the next draw.
 * @returns The session's own address, carrying the shape.
 */
export function fuxiNextPath(shape: SittingShape): string {
  const query = new URLSearchParams(sittingShapeFields(shape)).toString();

  return query === "" ? fuxiPath : `${fuxiPath}?${query}`;
}

/**
 * Where a sitting over one List 词单 is drawn, or a sitting over everything.
 *
 * The address a reader starts one from, which `/user/cidan/` links to, and
 * the address a swap and a restart put back in the bar so a reload keeps to
 * the List. See `src/user/list-study.ts`.
 *
 * @param list - The List's path under `/cidan/`, or nothing.
 * @returns The session's address, naming the List where there is one.
 */
export function fuxiListPath(list: string | undefined): string {
  return fuxiScopePath(list === undefined ? {} : { list });
}

/**
 * What a sitting is scoped to: a List, one question, an early review, any of
 * them or none.
 */
export type SittingScope = Pick<
  SittingShape,
  "list" | "focus" | "early" | "replay"
>;

/**
 * Where a sitting with this scope starts, with no hints on it.
 *
 * The address a swap and a restart put back in the bar, so a reload keeps to
 * the List, to the question a Focus Review asks and to an early review.
 *
 * @param scope - The List, the question and an early review, any of which may
 * be absent.
 * @returns The session's address, naming whatever the scope holds.
 */
export function fuxiScopePath(scope: SittingScope): string {
  return fuxiNextPath({ recent: [], ...scope });
}

/**
 * Where a Focus Review over one question starts, which 学习 links to.
 *
 * @param focus - The question it asks.
 * @returns The session's address, naming the question.
 */
export function fuxiFocusPath(focus: ReviewMode): string {
  return fuxiScopePath({ focus });
}

/**
 * Where an early review starts, over every question or over one.
 *
 * @param focus - The question a Focus Review asks, where the review keeps to it.
 * @returns The session's address, reaching a week ahead.
 */
export function fuxiEarlyPath(focus: ReviewMode | undefined): string {
  return fuxiScopePath({
    early: true,
    ...(focus === undefined ? {} : { focus }),
  });
}

/**
 * The region a press redraws, which is the card.
 *
 * The card alone, the day's figures having moved onto the navigation bar. Both
 * still change on every answer, so an answer carries the figures back beside
 * the card as an out-of-band swap. See `src/user/fuxi-markup.ts`.
 */
export const fuxiLiveId = "cb-fuxi-live";

/**
 * The day's figures on the bar, which an answer replaces out of band.
 *
 * A second id rather than a second region, since nothing fetches this on its
 * own. htmx finds the element by it and puts the copy in an answer carries
 * where the old one stood.
 */
export const fuxiTallyId = "cb-fuxi-tally";

/**
 * Where a reader with no session is sent to get one.
 *
 * The page names itself as the way back, on the `from` parameter `SignInStart`
 * reads and `safeReturnPath` checks.
 */
export const fuxiSignInLocation = `/user/sign-in/?from=${encodeURIComponent(fuxiPath)}`;

/**
 * Where a reader changes what their sessions do.
 *
 * A page of its own rather than a panel on the session, because a session is
 * one card at a time and a settings form is everything at once. A `GET` draws
 * the form and a `POST` saves it, at the one address, the way
 * `/user/settings/` answers itself.
 *
 * The cog on the session's bar and the one beside 学习's heading link here.
 * See `./fuxi-settings-link.ts`.
 */
export const fuxiSettingsPath = `${fuxiPath}settings/`;

/**
 * The form a save redraws, which is the whole of what a press changes.
 *
 * The form and nothing else. The heading and the line under it say what the
 * page is for and read the same after a save as before one, so they sit
 * outside it. `settingsLiveId` draws its own region the same way.
 */
export const fuxiSettingsLiveId = "cb-fuxi-settings-live";

/** Where a reader with no session is sent to get one, and come back here. */
export const fuxiSettingsSignInLocation = `/user/sign-in/?from=${encodeURIComponent(fuxiSettingsPath)}`;

/**
 * A replay of these cards, as the review history links to one.
 *
 * Undated, so the replay begins on the draw that first meets it. A link to a
 * replay of more than `replayAtMost` cards names the first of them.
 * @param cards - The cards, in the order to ask them.
 * @returns The address.
 */
export function fuxiReplayPath(cards: readonly CardSubject[]): string {
  return fuxiScopePath({ replay: { cards: cards.slice(0, replayAtMost) } });
}

/**
 * Where the review history 复习历史 is drawn.
 *
 * Under 复习 because it is 复习's record and leads back into it. 回顾 is the
 * summary of a window and links here for the whole of one.
 */
export const fuxiHistoryPath = `${fuxiPath}lishi/`;

/** What the history's address carries the window under, as 回顾's does. */
export const fuxiHistoryOverField = "over";

/** What the history's address carries "missed cards only" under. */
export const fuxiHistoryMissedField = "missed";

/** What the history's form posts each chosen card under. */
export const fuxiHistoryCardField = "card";

/** Where the history sends a reader who is not signed in. */
export const fuxiHistorySignInLocation = `/user/sign-in/?from=${encodeURIComponent(fuxiHistoryPath)}`;

/**
 * The address of one view of the history.
 *
 * @param view - The window, and whether only the missed cards are listed.
 * @returns The address.
 */
export function historyAddress(view: {
  period: HuiguPeriod;
  missedOnly: boolean;
}): string {
  const query = new URLSearchParams({
    [fuxiHistoryOverField]: view.period,
    ...(view.missedOnly ? { [fuxiHistoryMissedField]: "1" } : {}),
  });

  return `${fuxiHistoryPath}?${query.toString()}`;
}
